import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit
} from '@angular/core';
import { EnvelopeService } from '../services/envelope.service';
import { SentEnvelopeStatusCounter } from '../model/sent-envelope-status-counter';
import { SentEnvelopeFilter } from '../model/sent-envelope-filter';
import { SentEnvelopeStatus } from 'src/app/enum/sent-envelope-status';
import {
  LayoutService,
  InfiniteScrollOptions,
  ScrollDirection,
  IBlankslate,
  FilterService,
  SizeWatcherService,
  WindowSize
} from 'codex-lib';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { EnvelopeStructure } from '../model/envelope-structure';
import { EnvelopeFilter } from '../model/envelope-filter';
import { SortOrder } from 'src/app/enum/sort-order';
import { EnvelopeSortField } from 'src/app/enum/envelope-sort-field';
import { Envelope } from '../model/envelope';
import { ActivatedRoute } from '@angular/router';
import { ObjectUtil } from '../ObjectUtil';
import { ResolveSentDataModel } from 'src/app/model/resolve-sent-data-model';
import { AnalyticsService } from 'src/app/modules/google-analytics/service/analytics.service';
import { TrackerData } from 'src/app/modules/google-analytics/model/tracker-data.model';
import { AnalyticEventClient } from 'src/app/enum/analytic-event-client.enum';
import Segment from 'src/app/modules/settings/model/segment-model';
import { SurvicateService } from 'src/app/services/survicate.service';
import { BrowserUtil } from 'src/app/util/browser-util';
import { EnvelopeSortOption } from '../model/envelope-sort-option';
import { SentEnvelopeSortStorageService } from '../services/sent-envelope-sort-storage.service';

@Component({
  selector: 'app-sent',
  templateUrl: './sent.component.html',
  styleUrls: ['./sent.component.scss'],
})
export class SentComponent implements OnInit, AfterViewInit, OnDestroy {
  isIgnoreTabChange = true;
  blankslateOptions: IBlankslate;
  statusCounter: SentEnvelopeStatusCounter;
  pageSize: number;
  pageLimit: number;
  options: InfiniteScrollOptions = new InfiniteScrollOptions(false);
  filter: SentEnvelopeFilter;
  envelopeStructure: EnvelopeStructure;
  subscribeFilter: Subscription;
  loadingDisplay: Array<boolean>;
  oldViewType: boolean;
  subscribeWindowMode: Subscription;
  detailedEnvelopeId: number;
  isMobile: boolean = BrowserUtil.isMobile();
  sortOptions: EnvelopeSortOption[] = [];
  selectedSortOption: EnvelopeSortOption;
  sortOnboardingReady = false;

  private unsubscribeAll$ = new Subject<any>();
  private shouldResetInfiniteScrollAfterDataLoad = false;
  private infiniteScrollResetFrame: number | null = null;
  private hasTrackedInitialTab = false;
  private clickedTabStatus: SentEnvelopeStatus | null = null;
  private lastTrackedTabStatus: SentEnvelopeStatus | null = null;
  private lastTrackedTabAt = 0;
  private readonly tabAnalyticsDedupeIntervalMs = 250;

  constructor(
    private envelopeService: EnvelopeService,
    private layoutService: LayoutService,
    private filterService: FilterService,
    private sizeWatchService: SizeWatcherService,
    private route: ActivatedRoute,
    private googleAnalyticsService: AnalyticsService,
    private survicateService: SurvicateService,
    private sentEnvelopeSortStorageService: SentEnvelopeSortStorageService
  ) {
    this.envelopeStructure = new EnvelopeStructure();
    this.envelopeStructure.list = new Array<Envelope>();

    this.loadingDisplay = new Array<boolean>();

    this.route.data.subscribe((info) => {
      if (!ObjectUtil.isEmpty(info.data as ResolveSentDataModel)) {
        const _info: ResolveSentDataModel = info.data;
        this.statusCounter = _info.statusCounter;
        const newEnvelopeStructure = new EnvelopeStructure();
        newEnvelopeStructure.list = _info.list.slice();
        this.envelopeStructure = Object.assign({}, newEnvelopeStructure);
      }
    });

    this.filter = new SentEnvelopeFilter();
    this.filter.status = SentEnvelopeStatus.pending;
    this.pageSize = 0;
    this.pageLimit = 25;
    this.applySortSelectionForStatus(this.filter.status);

    this.layoutService.enableInfiniteScroll(this.options);

    this.layoutService.onInfiniteScroll$
      .pipe(takeUntil(this.unsubscribeAll$))
      .subscribe((direction: ScrollDirection) => {
        if (direction === ScrollDirection.DOWN) {
          this.getNextPage();
        }
      });

    this.subscribeWindowMode = this.sizeWatchService.windowMode.subscribe(
      (ws) => {
        if (ws === WindowSize.SMARTPHONE) {
          this.oldViewType = this.envelopeStructure.isList;
          this.onChangeType(false);
        } else {
          this.onChangeType(this.oldViewType);
        }
      }
    );
  }

  ngAfterViewInit(): void {
    this.survicateService.onInitPortal();
    setTimeout(() => {
      this.sortOnboardingReady = true;
    });
  }

  ngOnInit() {
    this.blankslateOptions = {
      icon: {
        prefix: 'fad',
        iconName: 'search-minus',
      },
      buttonText: null,
    };
  }

  ngOnDestroy() {
    if (this.subscribeFilter) {
      this.subscribeFilter.unsubscribe();
    }
    if (this.subscribeWindowMode) {
      this.subscribeWindowMode.unsubscribe();
    }
    this.cancelScheduledInfiniteScrollReset();
    this.layoutService.disableInfiniteScroll(this.options);
    this.unsubscribeAll$.next();
    this.unsubscribeAll$.complete();
    this.survicateService.finishedLoadingPortal = false;
  }

  totalEnvelopeCount(x) {
    let count = 0;
    count += x.completed;
    count += x.deleted;
    count += x.expired;
    count += x.onApproval;
    count += x.pending;
    count += x.reproved;
    count += x.toFill;
    count += x.rejected;
    return count;
  }

  getNextPage() {
    this.pageSize++;
    this.loadingDisplay.push(true);
    this.validateDisplayLoading();

    this.filter.skip = this.getSkipFilter();
    this.filter.take = this.getTakeFilter();

    this.subscribeFilter = this.envelopeService
      .filterElements(this.filter)
      .subscribe(this.filterData);
  }

  filterList(filter) {
    if (this.survicateService.finishedLoadingPortal) {
      this.loadingDisplay.push(true);
      this.validateDisplayLoading();

      this.pageSize = 0;
      filter.skip = 0;
      filter.take = 25;

      this.subscribeFilter = this.envelopeService
        .filterElements(filter)
        .subscribe(this.filterData);
    }
  }

  filterData = (data: Envelope[]) => {
    const shouldResetInfiniteScroll =
      this.pageSize === 0 && this.shouldResetInfiniteScrollAfterDataLoad;
    const newEnvelopeStructure = {
      list: this.pageSize > 0 ? this.envelopeStructure.list.concat(data) : data,
      isList: this.envelopeStructure.isList,
    };
    this.envelopeStructure = Object.assign({}, newEnvelopeStructure);
    this.validateBlankslateShowButton();
    this.loadingDisplay.pop();
    this.validateDisplayLoading();

    if (shouldResetInfiniteScroll) {
      this.shouldResetInfiniteScrollAfterDataLoad = false;
      this.scheduleInfiniteScrollReset();
    }
  };

  validateDisplayLoading() {
    this.layoutService.toggleLoading(this.loadingDisplay.length > 0);
  }

  onChangeType(isList: boolean) {
    const newEnvelopeStructure = {
      list: this.envelopeStructure.list,
      isList,
    };
    this.envelopeStructure = Object.assign({}, newEnvelopeStructure);
  }

  onTabChange(status: SentEnvelopeStatus) {
    this.trackTabSelectionAnalytics(status);

    this.layoutService.toggleRightAside(false);
    this.detailedEnvelopeId = null;
    this.filter.status = status;
    this.applySortSelectionForStatus(status);
    if (
      status !== SentEnvelopeStatus.pending ||
      (this.survicateService.finishedLoadingPortal && !this.isIgnoreTabChange)
    ) {
      this.survicateService.finishedLoadingPortal = true;
      this.pageSize = 0;
      this.reloadData();
    } else {
      this.isIgnoreTabChange = false; // must not ignore more tabchanges due first call onTabChange
    }
    this.onCheckAllClick(false);
  }

  onTabClick(status: SentEnvelopeStatus) {
    this.trackTabAnalytics(status);
    this.clickedTabStatus = status;
  }

  onCheckAllClick(checked: boolean) {
    this.envelopeStructure.list.map((env) => (env.checked = checked));
  }

  onSearchValueChange(value: string) {
    this.trackAnalyticsEvent('Signer_Enviado_Lupa');
    this.pageSize = 0;
    this.filter.textSearch = value;
    this.reloadData();
  }

  onClearSearch() {
    this.filter.textSearch = '';
    this.filter.skip = 0;
    this.filter.take = 25;
    this.filterService.setClearFilter(true);
    this.reloadData();
  }

  onBlankSlateButtonClick() {
    this.onClearSearch();
  }

  onSortChange(sortOption: EnvelopeSortOption) {
    this.trackAnalyticsEvent('Signer_Enviado_Ordenação', this.getSortLabel(sortOption));
    this.pageSize = 0;
    this.selectedSortOption = sortOption;
    this.filter.sortField = sortOption.sortField;
    this.filter.sortOrder = sortOption.sortOrder;
    this.sentEnvelopeSortStorageService.setForStatus(this.filter.status, {
      sortField: sortOption.sortField,
      sortOrder: sortOption.sortOrder
    });
    this.shouldResetInfiniteScrollAfterDataLoad = true;
    this.reloadData();
  }

  onFilterChange(filter: EnvelopeFilter) {
    if (!this.survicateService.finishedLoadingPortal || !filter) {
      return;
    }

    this.trackFilterAnalytics(filter);
    this.applyFilter(filter);
  }

  onFilterClear(filter: EnvelopeFilter) {
    if (!this.survicateService.finishedLoadingPortal || !filter) {
      return;
    }

    this.applyFilter(filter);
  }

  private applyFilter(filter: EnvelopeFilter): void {
    this.pageSize = 0;
    this.filter.creationDateRange = filter.creationDateRange;
    this.filter.expirationDateRange = filter.expirationDateRange;
    this.filter.conclutionDateRange = filter.conclutionDateRange;
    this.filter.segmentsIds = filter.segmentsIds;
    this.filter.creatorIds = filter.creatorIds;
    this.filter.creatorUserName = filter.creatorUserName;
    this.reloadData();
  }

  onFilterButtonClick() {
    this.trackAnalyticsEvent('Signer_Enviado_Filtro', 'Abrir filtro');
  }

  onShowEnvelopeDetails(envelopeId: number) {
    this.trackAnalyticsEvent('Signer_Enviado_Detalhar');
    this.detailedEnvelopeId = envelopeId;
    this.layoutService.toggleRightAside(true);
  }

  validateBlankslateShowButton() {
    this.blankslateOptions.buttonText =
      this.filter.textSearch || this.filter.expirationDateRange
        ? 'cleanSearch'
        : null;
  }

  onDiscardEnvelope() {
    this.reloadData();
  }

  reloadData() {
    this.layoutService.toggleRightAside(false);
    this.loadTabBadges();
    this.filterList(this.filter);
  }

  loadTabBadges() {
    this.loadingDisplay.push(true);
    this.validateDisplayLoading();
    this.envelopeService
      .getSentEnvelopeStatusCounter(this.filter)
      .subscribe(this.fillStatusCounterData);
  }

  fillStatusCounterData = (data: SentEnvelopeStatusCounter) => {
    this.statusCounter = Object.assign({}, data);
    this.loadingDisplay.pop();
    this.validateDisplayLoading();
  };

  onUpdateCardEnvelope(envelope: Envelope) {
    const index = this.envelopeStructure.list.indexOf(
      this.envelopeStructure.list.find(this.findIndexToUpdate, envelope.id)
    );
    this.envelopeStructure.list[index].expiration = envelope.expiration;
    this.envelopeStructure.list[index].segment = this.filterSegment(
      envelope.segment
    );
    this.reloadData();
  }

  findIndexToUpdate(newItem) {
    return newItem.id === this;
  }

  filterSegment(segment: Segment) {
    if (segment == null || segment == undefined) {
      const filterSegment = new Segment();
      filterSegment.id = 0;
      filterSegment.name = null;
      return filterSegment;
    }
    return segment;
  }

  private getSkipFilter() {
    return this.pageSize * this.pageLimit;
  }

  private getTakeFilter() {
    return this.pageLimit;
  }

  private resetInfiniteScroll(): void {
    this.resetInfiniteScrollContainer();
    this.options = new InfiniteScrollOptions(true);
    this.layoutService.disableInfiniteScroll(this.options);

    this.infiniteScrollResetFrame = requestAnimationFrame(() => {
      this.infiniteScrollResetFrame = null;
      this.options = new InfiniteScrollOptions(false);
      this.layoutService.enableInfiniteScroll(this.options);
    });
  }

  private scheduleInfiniteScrollReset(): void {
    this.cancelScheduledInfiniteScrollReset();

    this.infiniteScrollResetFrame = requestAnimationFrame(() => {
      this.infiniteScrollResetFrame = null;
      this.resetInfiniteScroll();
    });
  }

  private cancelScheduledInfiniteScrollReset(): void {
    if (this.infiniteScrollResetFrame === null) {
      return;
    }

    cancelAnimationFrame(this.infiniteScrollResetFrame);
    this.infiniteScrollResetFrame = null;
  }

  private resetInfiniteScrollContainer(): void {
    const content = document.getElementById(this.options.infiniteScrollContainer);

    if (!content) {
      return;
    }

    content.scrollTop = 0;
    content.style.height = '';
  }

  private applySortSelectionForStatus(status: SentEnvelopeStatus) {
    this.sortOptions = this.getSortOptions(status);

    const selection = this.sentEnvelopeSortStorageService.getForStatus(status);
    this.selectedSortOption = this.sortOptions.find((option) =>
      option.sortField === selection.sortField &&
      option.sortOrder === selection.sortOrder
    ) || this.sortOptions.find((option) =>
      option.sortField === EnvelopeSortField.title &&
      option.sortOrder === SortOrder.Ascending
    ) || this.sortOptions[0];

    this.filter.sortField = this.selectedSortOption.sortField;
    this.filter.sortOrder = this.selectedSortOption.sortOrder;
  }

  private getSortOptions(status: SentEnvelopeStatus): EnvelopeSortOption[] {
    const sortOptions: EnvelopeSortOption[] = [
      {
        label: 'envelope.list.sort.createdNewest',
        sortField: EnvelopeSortField.creationDate,
        sortOrder: SortOrder.Descending
      },
      {
        label: 'envelope.list.sort.createdOldest',
        sortField: EnvelopeSortField.creationDate,
        sortOrder: SortOrder.Ascending
      }
    ];

    if (status === SentEnvelopeStatus.completed) {
      sortOptions.push(
        {
          label: 'envelope.list.sort.completedNewest',
          sortField: EnvelopeSortField.conclutionDate,
          sortOrder: SortOrder.Descending
        },
        {
          label: 'envelope.list.sort.completedOldest',
          sortField: EnvelopeSortField.conclutionDate,
          sortOrder: SortOrder.Ascending
        }
      );
    }

    sortOptions.push(
      {
        label: 'envelope.list.sort.nameAsc',
        sortField: EnvelopeSortField.title,
        sortOrder: SortOrder.Ascending
      },
      {
        label: 'envelope.list.sort.nameDesc',
        sortField: EnvelopeSortField.title,
        sortOrder: SortOrder.Descending
      }
    );

    return sortOptions;
  }

  private trackFilterAnalytics(filter: EnvelopeFilter): void {
    const filterLabel = this.getFilterAnalyticsLabel(filter);

    this.trackAnalyticsEvent('Signer_Enviado_Filtro_Aplicar', filterLabel);

    this.getFilterTypeLabels(filter).forEach(label =>
      this.trackAnalyticsEvent('Signer_Enviado_Filtro_Tipo', label)
    );
  }

  private getFilterAnalyticsLabel(filter: EnvelopeFilter): string {
    const labels = this.getFilterTypeLabels(filter);

    return labels.length > 0 ? labels.join(' | ') : 'Sem filtro';
  }

  private getFilterTypeLabels(filter: EnvelopeFilter): string[] {
    if (!filter) {
      return [];
    }

    const labels: string[] = [];

    if (filter.creationDateRange) {
      labels.push('Criação');
    }

    if (filter.expirationDateRange) {
      labels.push('Expiração');
    }

    if (filter.conclutionDateRange) {
      labels.push('Conclusão');
    }

    if (filter.segmentsIds && filter.segmentsIds.length > 0) {
      labels.push('Segmento');
    }

    if (filter.creatorIds != null) {
      labels.push('Criador');
    }

    return labels;
  }

  private getTabLabel(status: SentEnvelopeStatus): string {
    switch (status) {
      case SentEnvelopeStatus.pending:
        return 'Em Assinatura';
      case SentEnvelopeStatus.toFill:
        return 'Em preenchimento';
      case SentEnvelopeStatus.onApproval:
        return 'Em autorização';
      case SentEnvelopeStatus.completed:
        return 'Concluídos';
      case SentEnvelopeStatus.expired:
        return 'Expirados';
      case SentEnvelopeStatus.deleted:
        return 'Excluídos';
      case SentEnvelopeStatus.reproved:
        return 'Reprovados';
      case SentEnvelopeStatus.rejected:
        return 'Rejeitados';
      default:
        return '';
    }
  }

  private getSortLabel(sortOption: EnvelopeSortOption): string {
    const sortType = sortOption.sortField === EnvelopeSortField.title ? 'Nome' : 'Data';
    const sortDirection = sortOption.sortOrder === SortOrder.Ascending ? 'Crescente' : 'Decrescente';
    return `${sortType} ${sortDirection}`;
  }

  private trackTabSelectionAnalytics(status: SentEnvelopeStatus): void {
    if (this.clickedTabStatus === status) {
      this.clickedTabStatus = null;
      return;
    }

    if (this.hasTrackedInitialTab && this.filter.status === status) {
      return;
    }

    this.trackTabAnalytics(status);
  }

  private trackTabAnalytics(status: SentEnvelopeStatus): void {
    const now = Date.now();

    if (
      this.lastTrackedTabStatus === status &&
      now - this.lastTrackedTabAt < this.tabAnalyticsDedupeIntervalMs
    ) {
      return;
    }

    this.hasTrackedInitialTab = true;
    this.lastTrackedTabStatus = status;
    this.lastTrackedTabAt = now;
    this.trackAnalyticsEvent('Signer_Enviado_Abas', this.getTabLabel(status));
  }

  private trackAnalyticsEvent(action: string, label?: string): void {
    this.googleAnalyticsService.sendEvent(
      new TrackerData(AnalyticEventClient.TK_PT_SIGNER_SENT, action, label)
    );
  }
}
